import React, { useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faEye, faEyeSlash } from "@fortawesome/pro-light-svg-icons"
import { FormattedMessage } from "react-intl"
import "./PasswordInput.css"

/**
 * Lets a user toggle whether the inputted password is visible/hidden, and uses
 * a tooltip to indicate whether the password meets our requirements
 * @prop {string} password - The current value of the password input
 * @prop {function} onPasswordChange - Function to update the current password state
 * @prop {string} className - Allows custom styling of the input element
 * @prop {string} id - The html id of the password input element
 * @prop {boolean} isNew - Whether this password input is for a new password. If
 * true, triggers the display of the tooltip with password requirements
 */
const PasswordInput = ({password="", onPasswordChange, className="", id, isNew=false}) => {
  const [passwordVisible, setPasswordVisible] = useState(false)

  const onPasswordInput = async (e) => {
    const newPassword = e.target.value
    onPasswordChange(newPassword)
  }

  const requirements = [
    {
      label: <FormattedMessage id="password-input-length" defaultMessage="At least 8 characters" />,
      met: password.length >= 8,
    },
    {
      label: <FormattedMessage id="password-input-number" defaultMessage="Number" />,
      met: /\d/.test(password),
    },
    {
      label: <FormattedMessage id="password-input-letter" defaultMessage="Letter" />,
      met: /[a-z]/i.test(password),
    },
  ]

  return (
    <div className="password-input-container">
      <input
        className={`password-input ${className}`}
        id={id} 
        type={passwordVisible ? "text" : "password"}
        autoComplete={isNew ? "new-password" : "current-password"}
        required
        pattern="^(?=.*[a-zA-Z])(?=.*[0-9]).+$"
        minLength={8}
        value={password}
        onInput={onPasswordInput}
      />
      <button type="button" onClick={() => setPasswordVisible(!passwordVisible)} className="password-visibility-toggle">
        <FontAwesomeIcon icon={passwordVisible ? faEyeSlash : faEye} color="#363636" />
      </button>
      {isNew && (
        <div className="password-requirements-tooltip" role="tooltip">
          <ul className="password-input-requirements">
            {requirements.map((req, idx) => (
              <li key={idx} className={req.met ? "satisfied-requirement" : "unsatisfied-requirement"}>
                {req.label}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

export default PasswordInput
